/**
 * useMarkNotificationsRead — persists the caller's read watermark via
 * `xan_mark_notifications_read` and invalidates the activity feed cache.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { parseNotificationEnvelope } from "@/features/notifications/envelope";
import { activityKeys } from "@/features/notifications/hooks/query-keys";
import { getBackendClient } from "@/lib/backend";

const backend = getBackendClient();

/**
 * Both watermark fields are nullable: an empty activity log with no prior read
 * state records no tuple, so the RPC returns SQL NULL for each.
 */
const markNotificationsReadResponseSchema = z.object({
  last_read_at: z.string().nullable(),
  last_read_id: z.string().nullable(),
  success: z.boolean(),
});

export interface NotificationsReadWatermark {
  lastReadAt: string | null;
  lastReadId: string | null;
  success: boolean;
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<NotificationsReadWatermark> => {
      const { data, error } = await backend.data.rpc("xan_mark_notifications_read", {});

      if (error) {
        throw error;
      }

      const response = parseNotificationEnvelope(
        "mark notifications read",
        markNotificationsReadResponseSchema,
        data,
      );

      return {
        lastReadAt: response.last_read_at,
        lastReadId: response.last_read_id,
        success: response.success,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
  });
}
